import React, { useState, useEffect } from 'react';
import { X, Shield, Download, FileText, Check, Lock, Landmark, MapPin, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_URL as API } from '../config/api';
import logo from '../assets/logo.png';

export default function AgreementModal({
  isOpen,
  onClose,
  agreementType = 'customer',
  userAgreement = null,
  mandatoryAccept = false,
  onAccept = null,
  postReg = false,
  actionButtonText = '',
  loadingAction = false
}) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [activeTab, setActiveTab] = useState('agreement'); // 'agreement' | 'privacy' | 'terms'

  const isPartner = agreementType === 'partner' || userAgreement?.agreement_type === 'partner_agreement';

  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
      setAcceptError('');
      setActiveTab('agreement');
      if (!userAgreement) {
        setLoading(true);
        const typeParam = isPartner ? 'partner' : 'customer';
        axios.get(`${API}/agreements/template/${typeParam}`)
          .then(res => setTemplate(res.data))
          .catch(err => console.error("Failed to load template", err))
          .finally(() => setLoading(false));
      }
    }
  }, [isOpen, agreementType, isPartner, userAgreement]);

  if (!isOpen) return null;

  const downloadUrl = userAgreement?.download_url || (userAgreement?.id ? `${API}/agreements/download/${userAgreement.id}` : `${API}/agreements/download/template_${isPartner ? 'partner' : 'customer'}`);

  const handleConfirmAccept = () => {
    if (!acknowledged) {
      setAcceptError('Please click the checkbox to place a checkmark (✓) confirming your agreement before proceeding.');
      return;
    }
    setAcceptError('');
    if (onAccept) {
      onAccept();
    } else if (onClose) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !mandatoryAccept) {
      onClose?.();
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '1rem',
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div style={{
        background: '#ffffff',
        borderRadius: '0px',
        maxWidth: '920px',
        width: '100%',
        height: '78vh',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.35)',
        border: '1px solid #d1d5db',
        overflow: 'hidden',
        fontFamily: "'Inter', sans-serif"
      }}>
        {/* Header - White Paper Style with Logo on Top Left Corner */}
        <div style={{
          background: '#ffffff',
          color: '#000000',
          padding: '1.25rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb',
          borderRadius: '0px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {/* Logo in Top Left Corner */}
            <img
              src={logo}
              alt="TurfX Logo"
              style={{
                height: '38px',
                width: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
            <div style={{ width: '1px', height: '32px', background: '#e5e7eb' }} />
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800', color: '#000000', letterSpacing: '-0.3px' }}>
                {isPartner ? 'Venue Partner & Settlement Agreement' : 'Customer Service & Data Privacy Agreement'}
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: '#4b5563', fontWeight: '500' }}>
                {userAgreement?.signature_id ? `Digitally Signed Record #${userAgreement.signature_id}` : (mandatoryAccept ? 'Mandatory Digital Contract Acceptance' : 'Electronic Master Contract & Policy Terms')}
              </p>
            </div>
          </div>
          {!mandatoryAccept && (
            <button
              onClick={onClose}
              style={{
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '0px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#000000',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; }}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Tab Navigation Switcher - Plain White Paper Style */}
        <div style={{
          display: 'flex',
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          overflowX: 'auto'
        }}>
          <button
            type="button"
            onClick={() => setActiveTab('agreement')}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: '#ffffff',
              color: activeTab === 'agreement' ? '#000000' : '#6b7280',
              border: 'none',
              borderRadius: '0px',
              fontWeight: activeTab === 'agreement' ? '800' : '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s',
              borderBottom: activeTab === 'agreement' ? '2px solid #000000' : '2px solid transparent'
            }}
          >
            <FileText size={15} color={activeTab === 'agreement' ? '#000000' : '#6b7280'} /> {isPartner ? '1. Partner Agreement' : '1. Customer Agreement'}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('privacy')}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: '#ffffff',
              color: activeTab === 'privacy' ? '#000000' : '#6b7280',
              border: 'none',
              borderRadius: '0px',
              fontWeight: activeTab === 'privacy' ? '800' : '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s',
              borderBottom: activeTab === 'privacy' ? '2px solid #000000' : '2px solid transparent'
            }}
          >
            <Lock size={15} color={activeTab === 'privacy' ? '#000000' : '#6b7280'} /> 2. Privacy Policy
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('terms')}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: '#ffffff',
              color: activeTab === 'terms' ? '#000000' : '#6b7280',
              border: 'none',
              borderRadius: '0px',
              fontWeight: activeTab === 'terms' ? '800' : '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s',
              borderBottom: activeTab === 'terms' ? '2px solid #000000' : '2px solid transparent'
            }}
          >
            <Shield size={15} color={activeTab === 'terms' ? '#000000' : '#6b7280'} /> 3. Terms & Conditions
          </button>
        </div>

        {/* Scrollable Content - White Paper & Black Text */}
        <div style={{ padding: '1.75rem', overflowY: 'auto', flex: 1, background: '#ffffff', color: '#000000' }}>
          
          {/* Post-Registration Notice */}
          {postReg && (
            <div style={{
              background: '#ffffff',
              border: '1px solid #000000',
              borderRadius: '0px',
              padding: '1rem 1.25rem',
              marginBottom: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{ background: '#000000', color: '#ffffff', borderRadius: '0px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Check size={16} strokeWidth={3} />
              </div>
              <div>
                <div style={{ fontWeight: '800', color: '#000000', fontSize: '0.92rem' }}>
                  Please Review & Confirm Your Agreement
                </div>
                <div style={{ fontSize: '0.82rem', color: '#374151', marginTop: '2px', lineHeight: '1.4' }}>
                  In compliance with the Information Technology Act (2000), please review the agreement tabs below and click the checkbox to complete registration.
                </div>
              </div>
            </div>
          )}

          {/* Certificate Badge if signed */}
          {userAgreement?.signature_id && (
            <div style={{
              background: '#ffffff',
              border: '1px solid #000000',
              borderRadius: '0px',
              padding: '0.9rem 1.2rem',
              marginBottom: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#000000', fontWeight: '800', fontSize: '0.88rem' }}>
                  <Check size={16} color="#000000" strokeWidth={3} /> Digitally Signed & Verified Electronic Record
                </div>
                <div style={{ fontSize: '0.78rem', color: '#4b5563', marginTop: '2px' }}>
                  Certificate ID: <strong style={{ color: '#000000' }}>{userAgreement.signature_id}</strong> | Signed at: {userAgreement.metadata?.signed_at_ist || new Date(userAgreement.signed_at).toLocaleString()}
                </div>
              </div>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#ffffff',
                  color: '#000000',
                  border: '1px solid #000000',
                  padding: '7px 12px',
                  borderRadius: '0px',
                  fontSize: '0.78rem',
                  fontWeight: '800',
                  textDecoration: 'none'
                }}
              >
                <Download size={13} /> Download PDF
              </a>
            </div>
          )}

          {/* TAB 1: MASTER AGREEMENT */}
          {activeTab === 'agreement' && (
            isPartner ? (
              <div>
                <Section title="1. Merchant Onboarding & Venue Listing Standards" icon={<FileText size={17} color="#000000" />}>
                  Venue operators agree to list genuine sports facilities, accurate court dimensions, artificial turf/court specifications (FIFA approved turf, wooden badminton court, acrylic tennis), realistic photographs, and transparent hourly slot pricing. All listed pricing must include statutory taxes where applicable.
                </Section>
                <Section title="2. Information We Collect from Venue Partners" icon={<Shield size={17} color="#000000" />}>
                  We collect business legal entity information, registered trade name, proprietor/partner/director credentials (mobile, email, address), venue location coordinates (GPS mapping), high-resolution facility pictures, operational hours, slot pricing matrices, and authorized staff access profiles.
                </Section>
                <Section title="3. Mandatory Bank Details Collection & Settlement Payouts" icon={<Landmark size={17} color="#000000" />}>
                  <strong>Why bank details are required:</strong> Venue Partners must submit a verified bank account number, account holder name, and IFSC code during onboarding. Submitting bank details is mandatory for:
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '1.2rem', lineHeight: '1.6' }}>
                    <li><strong>Automated Revenue Disbursals:</strong> Transferring net booking earnings directly to your registered bank account on daily/weekly settlement cycles.</li>
                    <li><strong>Tax & Statutory Compliance:</strong> Deducting and filing statutory Tax Deducted at Source (TDS under Section 194-O of the Income Tax Act) and issuing audited settlement invoices.</li>
                    <li><strong>Cancellation Compensation Payouts:</strong> Automatically remitting 50% compensation payouts directly to your bank account when players cancel within the 6-hour non-refundable window.</li>
                  </ul>
                </Section>
                <Section title="4. 5% Vendor Commission, Platform Fee & Financial Settlements" icon={<Lock size={17} color="#000000" />}>
                  <strong>Merchant Commission:</strong> TurfX charges and deducts a <strong>5% platform fee / commission</strong> (+ 18% GST on the platform fee) from the gross court fee on every booking fulfilled through TurfX. All player payments are collected via RBI-authorized, PCI-DSS certified encrypted payment gateways. TurfX absorbs payment collection chargeback risks for valid bookings.
                </Section>
                <Section title="5. Tournament Host Fees & Prize Pool Settlements" icon={<FileText size={17} color="#000000" />}>
                  <strong>Tournament Registration & Commission:</strong> For venue partners hosting or organizing tournaments on TurfX, a <strong>5% platform tournament facilitation commission</strong> is deducted from total tournament entry fee collections to support automated tournament bracket management, live fixture tracking, umpire dashboards, and verified prize pool payouts.
                </Section>
                <Section title="6. Booking Fulfillment, Overbooking Prohibition & Slot Honor Guarantee" icon={<Check size={17} color="#000000" strokeWidth={3} />}>
                  Venue partners must unconditionally honor every confirmed booking made via TurfX. Offline double-booking, refusal of court access, or demand for unlisted cash fees at the venue gate is strictly prohibited and constitutes a breach of contract subject to financial penalties or immediate platform delisting.
                </Section>
                <Section title="7. Facility Upkeep, Ground Safety & Operational Standards" icon={<Shield size={17} color="#000000" />}>
                  Partners agree to maintain high facility standards including clean artificial grass fibers, balanced rubber infill, functional LED floodlights for night games, well-maintained boundary safety netting, accessible first-aid kits, hygienic drinking water, and clean restroom amenities.
                </Section>
                <Section title="8. Dispute Resolution, Indemnity & Jurisdiction" icon={<FileText size={17} color="#000000" />}>
                  Venue partners agree to indemnify and hold harmless TurfX and its parent entity against any on-ground physical injuries or property damages occurring at the venue. This Agreement is governed by the laws of the Republic of India with exclusive legal jurisdiction in Chitradurga / Bengaluru, Karnataka.
                </Section>
              </div>
            ) : (
              <div>
                <Section title="1. Account Registration & Personal Data Collection" icon={<FileText size={17} color="#000000" />}>
                  To facilitate sports venue reservations and verify player credentials, TurfX collects personal data including your full name, verified mobile phone number, active email address, encrypted authentication credentials, approximate/precise GPS location (to find nearest turfs), and device diagnostic data.
                </Section>
                <Section title="2. Why and How We Use Your Data" icon={<Shield size={17} color="#000000" />}>
                  Your data is processed strictly for legitimate sports booking operations:
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '1.2rem', lineHeight: '1.6' }}>
                    <li><strong>Slot Reservation & Match Passes:</strong> Locking selected court time in real-time and generating tamper-proof QR code match entry passes.</li>
                    <li><strong>Automated Notifications:</strong> Transmitting booking confirmations, slot reminders, rainout reschedule updates, and refund notifications via SMS/WhatsApp/Email.</li>
                    <li><strong>Customer Support & Disputed Claims:</strong> Investigating attendance logs, processing reschedule tickets, and ensuring fair dispute resolution.</li>
                    <li><strong>Security & Legal Compliance:</strong> Maintaining audit trails complying with the Information Technology Act (2000) and cybersecurity guidelines.</li>
                  </ul>
                </Section>
                <Section title="3. Payment Gateway Security & Encrypted Processing" icon={<Lock size={17} color="#000000" />}>
                  All monetary transactions on TurfX are processed securely through RBI-authorized, 256-bit SSL encrypted payment gateways. TurfX adheres strictly to data minimization and tokenization standards: we NEVER capture, store, or view your debit/credit card numbers, CVVs, or netbanking passwords on our servers.
                </Section>
                <Section title="4. 5% Customer Booking Commission & Tournament Entry Fees" icon={<Lock size={17} color="#000000" />}>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', lineHeight: '1.6' }}>
                    <li><strong>5% Booking Platform Commission:</strong> TurfX charges a <strong>5% platform fee / convenience commission</strong> (+ applicable GST) on each court slot booking to cover cloud infrastructure, instant slot synchronization, and payment gateway routing.</li>
                    <li><strong>Tournament Registration Fees:</strong> For tournament registrations, players or participating teams pay the designated tournament entry fee plus a <strong>5% tournament facilitation commission</strong> covering digital fixture draws, live scorecards, and verified prize pool disbursements.</li>
                  </ul>
                </Section>
                <Section title="5. Bank Account Details Collection Policy for Players" icon={<Landmark size={17} color="#000000" />}>
                  <strong>Why bank details are collected:</strong> Customers may voluntarily provide their bank account number, account holder name, and IFSC code or UPI ID. These details are stored in encrypted format and used exclusively for:
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '1.2rem', lineHeight: '1.6' }}>
                    <li><strong>Direct-to-Bank Refunds:</strong> Disbursing cash refunds when bookings are cancelled due to severe weather, facility maintenance, or partner unavailability.</li>
                    <li><strong>Wallet Balance Cash-Out:</strong> Withdrawing accumulated tournament prize earnings and referral credits directly to your verified savings account.</li>
                  </ul>
                </Section>
                <Section title="6. Booking, Cancellation & Rescheduling Rules" icon={<Check size={17} color="#000000" strokeWidth={3} />}>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', lineHeight: '1.6' }}>
                    <li><strong>Cancellations More Than 6 Hours Prior (&gt; 6 hrs):</strong> Eligible for a full 100% refund of base court fees credited to your TurfX wallet or source account.</li>
                    <li><strong>Cancellations Under 6 Hours Prior (&lt; 6 hrs):</strong> Non-refundable to compensate venue operators for reserved court blocking.</li>
                    <li><strong>Rescheduling:</strong> One-time date/time slot rescheduling is permitted subject to court availability and partner approval.</li>
                  </ul>
                </Section>
                <Section title="7. Player Code of Conduct & Ground Safety" icon={<Shield size={17} color="#000000" />}>
                  Players must wear appropriate sports footwear (non-marking studs, turf shoes, or athletic sneakers) and adhere strictly to sportsmanship rules. Any violence, vandalism, substance abuse, smoking, or unauthorized sub-letting of slots will result in immediate termination of account access and ground eviction without refund.
                </Section>
                <Section title="8. Limitation of Liability, Governing Law & Jurisdiction" icon={<FileText size={17} color="#000000" />}>
                  TurfX acts as a digital intermediary platform connecting players with independent sports turf facilities. Sports and physical activities carry inherent risks of injury; players assume full voluntary risk for personal health and safety. This agreement is governed by the laws of India, subject to the exclusive jurisdiction of the competent courts in Karnataka.
                </Section>
              </div>
            )
          )}

          {/* TAB 2: PRIVACY POLICY */}
          {activeTab === 'privacy' && (
            <div>
              <Section title="1. Privacy Commitment & Scope" icon={<Lock size={17} color="#000000" />}>
                MetaQode Technologies Pvt. Ltd. ("TurfX") respects your privacy. This policy covers the collection, storage, encryption, and handling of user and vendor personal data across the TurfX website and mobile apps.
              </Section>
              <Section title="2. Categories of Data Collected" icon={<FileText size={17} color="#000000" />}>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', lineHeight: '1.6' }}>
                  <li><strong>Personal Identifiers:</strong> Name, phone number (OTP verified), email address, encrypted login credentials.</li>
                  <li><strong>Financial & Transaction Identifiers:</strong> Encrypted payment gateway transaction reference codes, wallet transaction histories, and voluntary bank account/UPI details for refunds and tournament payouts.</li>
                  <li><strong>Telemetry & Geolocation:</strong> Device type, OS version, and approximate/precise GPS coordinates used solely to discover sports venues nearby.</li>
                </ul>
              </Section>
              <Section title="3. Third-Party Sharing & Data Minimization" icon={<Shield size={17} color="#000000" />}>
                We do NOT sell, rent, or trade your personal data. Data is shared exclusively with verified venue managers (to validate your gate check-in pass) and RBI-authorized payment gateways to execute financial transactions securely.
              </Section>
              <Section title="4. Data Retention & Erasure Rights" icon={<Check size={17} color="#000000" strokeWidth={3} />}>
                Users may request account deactivation and personal data erasure by submitting a written request to support@metaqode.co.in, subject to statutory tax and auditing retention obligations under Indian law.
              </Section>
            </div>
          )}

          {/* TAB 3: TERMS & CONDITIONS */}
          {activeTab === 'terms' && (
            <div>
              <Section title="1. Platform Commission & Fee Structure" icon={<Lock size={17} color="#000000" />}>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', lineHeight: '1.6' }}>
                  <li><strong>Customer Booking Commission:</strong> 5% platform convenience fee (+ applicable GST) added on court bookings.</li>
                  <li><strong>Vendor Settlement Commission:</strong> 5% platform commission (+ 18% GST on platform fee) deducted from vendor booking revenues prior to automated bank disbursal.</li>
                  <li><strong>Tournament Entry Commission:</strong> 5% facilitation fee applied on tournament player registrations and deducted on organizer prize pool collections.</li>
                </ul>
              </Section>
              <Section title="2. Account Eligibility & Responsibilities" icon={<FileText size={17} color="#000000" />}>
                Users must provide truthful information during signup and keep credentials secure. Any unauthorized account activity must be reported immediately to TurfX support.
              </Section>
              <Section title="3. Cancellation, Rescheduling & Refund Timelines" icon={<Check size={17} color="#000000" strokeWidth={3} />}>
                Cancellations made more than 6 hours prior to the slot time receive a 100% base booking refund. Cancellations made within 6 hours are non-refundable (50% is disbursed to the venue partner as compensation). Rescheduling is allowed once per booking.
              </Section>
              <Section title="4. Limitation of Intermediary Liability" icon={<Shield size={17} color="#000000" />}>
                TurfX is a technology facilitator connecting sports players with venue owners under Section 79 of the IT Act. TurfX is not responsible for physical injuries, ground defects, or lost equipment on turf premises.
              </Section>
            </div>
          )}

          {/* Official Signed Digital Certificate & Parties Execution Block (Visible when viewed as a Signed Agreement) */}
          {userAgreement?.signature_id && (
            <div style={{
              background: '#ffffff',
              border: '1px solid #000000',
              borderRadius: '0px',
              padding: '1.25rem 1.5rem',
              marginTop: '1.5rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #000000',
                paddingBottom: '10px',
                marginBottom: '12px',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#000000', fontWeight: '800', fontSize: '0.92rem', letterSpacing: '0.5px' }}>
                  <Shield size={18} color="#000000" /> [OFFICIALLY VERIFIED] DIGITALLY SIGNED CONTRACT EXECUTION
                </div>
                <span style={{ background: '#000000', color: '#ffffff', fontWeight: '700', fontSize: '0.72rem', padding: '3px 8px' }}>
                  SEC 4 & 5 IT ACT (2000) COMPLIANT
                </span>
              </div>

              {/* Two Parties Summary Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                {/* Party 1: The Signatory */}
                <div style={{ background: '#ffffff', border: '1px solid #d1d5db', padding: '12px 14px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#4b5563', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Party A — {isPartner ? 'Venue Partner / Vendor Signatory' : 'Customer / Player Signatory'}
                  </div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#000000' }}>
                    {userAgreement.user_name || userAgreement.metadata?.name || 'Registered Partner'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#111827', marginTop: '4px' }}>
                    Role: <strong>{isPartner ? 'Venue Operator / Merchant' : 'Customer / Player'}</strong>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#111827', marginTop: '2px' }}>
                    Phone: <strong>{userAgreement.user_phone || userAgreement.metadata?.phone || 'N/A'}</strong>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#111827', marginTop: '2px' }}>
                    Email: <strong>{userAgreement.user_email || userAgreement.metadata?.email || 'N/A'}</strong>
                  </div>
                </div>

                {/* Party 2: Platform Entity */}
                <div style={{ background: '#ffffff', border: '1px solid #d1d5db', padding: '12px 14px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#4b5563', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Party B — Platform Intermediary & Technology Provider
                  </div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#000000' }}>
                    MetaQode Technologies Pvt. Ltd. (TurfX)
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#111827', marginTop: '4px' }}>
                    CIN / Reg: <strong>Chitradurga - 577501, Karnataka</strong>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#111827', marginTop: '2px' }}>
                    Contact: <strong>+91 9980998199 | support@metaqode.co.in</strong>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#111827', marginTop: '2px' }}>
                    Platform URL: <strong>https://turfx.in</strong>
                  </div>
                </div>
              </div>

              {/* Digital Hash and Consent Details */}
              <div style={{ background: '#ffffff', border: '1px solid #d1d5db', padding: '10px 14px', fontSize: '0.8rem', color: '#111827' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                  <div>Digital Certificate ID: <strong style={{ color: '#000000' }}>{userAgreement.signature_id}</strong></div>
                  <div>Signature Timestamp: <strong style={{ color: '#000000' }}>{userAgreement.metadata?.signed_at_ist || new Date(userAgreement.signed_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</strong></div>
                </div>
                <div>
                  Consent Statement: <strong style={{ color: '#000000' }}>"I have read and acknowledge that I accept the above terms and conditions."</strong>
                </div>
              </div>
            </div>
          )}

          {/* Interactive Checkbox Box (Only visible for new signups/unacknowledged agreements) */}
          {!userAgreement?.signature_id && (
            <div style={{
              background: '#ffffff',
              border: `1px solid ${acceptError ? '#dc2626' : '#000000'}`,
              borderRadius: '0px',
              padding: '1.25rem 1.5rem',
              marginTop: '1.5rem',
              transition: 'all 0.2s'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#000000', fontWeight: '800', fontSize: '0.92rem', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Check size={18} color="#000000" strokeWidth={3} /> Electronic Consent & Acceptance
              </div>

              {/* REAL CHECKBOX CONTAINER */}
              <div
                onClick={() => {
                  const next = !acknowledged;
                  setAcknowledged(next);
                  if (next) setAcceptError('');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '14px',
                  cursor: 'pointer',
                  padding: '12px 16px',
                  background: '#ffffff',
                  border: `1px solid ${acknowledged ? '#000000' : '#d1d5db'}`,
                  borderRadius: '0px',
                  userSelect: 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                {/* Box that displays Right Mark (✓) */}
                <div
                  style={{
                    width: '22px',
                    height: '22px',
                    minWidth: '22px',
                    borderRadius: '0px',
                    border: '1.5px solid #000000',
                    background: acknowledged ? '#000000' : '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '2px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {acknowledged && <Check size={16} color="#ffffff" strokeWidth={3.5} />}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ color: '#000000', fontSize: '0.92rem', fontWeight: '800', lineHeight: '1.5' }}>
                    I have read and acknowledge that I accept the above terms and conditions.
                  </div>
                  <div style={{ color: '#4b5563', fontSize: '0.82rem', marginTop: '4px', lineHeight: '1.5' }}>
                    I provide affirmative electronic consent to the {isPartner ? 'Venue Partner Agreement & Payout terms' : 'Customer Agreement'},{' '}
                    <Link to="/privacy-policy" target="_blank" onClick={e => e.stopPropagation()} style={{ color: '#000000', fontWeight: '700', textDecoration: 'underline' }}>Privacy Policy</Link>, and{' '}
                    <Link to="/terms-and-conditions" target="_blank" onClick={e => e.stopPropagation()} style={{ color: '#000000', fontWeight: '700', textDecoration: 'underline' }}>Terms & Conditions</Link>.
                  </div>
                </div>
              </div>

              {acceptError && (
                <div style={{ color: '#dc2626', fontSize: '0.85rem', fontWeight: '700', marginTop: '10px', background: '#fef2f2', padding: '8px 12px', border: '1px solid #fecaca' }}>
                  {acceptError}
                </div>
              )}

              <p style={{ margin: '10px 0 0 0', color: '#4b5563', fontSize: '0.78rem', lineHeight: '1.4' }}>
                Legally recognized electronic contract under Sections 4 & 5 of the Information Technology Act, 2000 (India).
              </p>
            </div>
          )}

          {/* MetaQode Address & Contact Details - Plain White Paper Style */}
          <div style={{
            background: '#ffffff',
            color: '#000000',
            borderRadius: '0px',
            padding: '1.25rem 1.5rem',
            marginTop: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontWeight: '800', fontSize: '0.92rem', color: '#000000' }}>
              MetaQode Technologies Pvt. Ltd. (Parent & Operating Entity)
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.82rem', color: '#4b5563' }}>
              <MapPin size={15} color="#000000" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Registered Office: Chitradurga - 577501, Karnataka, India</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', fontSize: '0.82rem', color: '#4b5563', marginTop: '2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={14} color="#000000" />
                <span>support@metaqode.co.in</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Phone size={14} color="#000000" />
                <span>+91 9980998199</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions - Plain White Paper Style */}
        <div style={{
          padding: '1.25rem 2rem',
          borderTop: '1px solid #e5e7eb',
          background: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          borderRadius: '0px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: '12px' }}>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: '#000000',
                background: '#ffffff',
                border: '1px solid #000000',
                padding: '11px 22px',
                borderRadius: '0px',
                fontWeight: '800',
                fontSize: '0.88rem',
                textDecoration: 'none',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; }}
            >
              <Download size={16} /> {userAgreement?.signature_id ? 'Download Signed Agreement PDF' : 'Download Official PDF'}
            </a>

            {mandatoryAccept && (
              <button
                type="button"
                disabled={loadingAction}
                onClick={handleConfirmAccept}
                style={{
                  background: acknowledged ? '#000000' : '#9ca3af',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0px',
                  padding: '14px 28px',
                  fontWeight: '800',
                  fontSize: '0.92rem',
                  cursor: (acknowledged && !loadingAction) ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                <Check size={18} strokeWidth={3.5} />
                {loadingAction ? 'Processing...' : (actionButtonText || (isPartner ? 'Accept Agreement & Complete Registration →' : 'I Agree & Create Account →'))}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div style={{ marginBottom: '1.35rem' }}>
      <h4 style={{
        margin: '0 0 6px 0',
        fontSize: '0.95rem',
        fontWeight: '800',
        color: '#000000',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {icon} {title}
      </h4>
      <div style={{ margin: 0, fontSize: '0.88rem', color: '#1f2937', lineHeight: '1.7' }}>
        {children}
      </div>
    </div>
  );
}
